import { data } from 'react-router';

import { useTrackPageView } from '#/hooks/useTrackPageView';
import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import { apiServerFetchUserProfile } from '#/modules/userProfile/api/apiFetchUserProfile';
import { viewUserProfileRoute } from '#/modules/userProfile/routing/userProfile.routes';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewUserProfilePage } from './ViewUserProfilePage';
import { ViewUserProfilePageNotFound } from './ViewUserProfilePageNotFound';

export async function loader({
  request,
  context,
  params: { username },
}: HorizonAppRouteLoaderArgs<typeof viewUserProfileRoute>) {
  const fetchedUserProfile = await apiServerFetchUserProfile({
    request,
    context,
  })(username).catch(() => null);

  const userProfile =
    fetchedUserProfile?.type === 'USER' ? fetchedUserProfile : null;

  return data({ userProfile }, { status: userProfile ? 200 : 404 });
}

export default function ViewUserProfileHorizonRoute({
  params: { username },
  loaderData: { userProfile },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();
  useTrackPageView('User Profile', { username });

  if (!userProfile) {
    return (
      <RouteModule title={i18n('Not Found')}>
        <ViewUserProfilePageNotFound />
      </RouteModule>
    );
  }

  return (
    <RouteModule
      title={`${userProfile.name} (@${userProfile.username})`}
      ogImage={imageUrlForOgMeta({ data: userProfile.image })}
      linkRels={{
        canonical: viewUserProfileRoute.with({
          username: userProfile.username,
        }),
      }}
    >
      <ViewUserProfilePage initialUserProfile={userProfile} />
    </RouteModule>
  );
}
